import {
  Box,
  ColorSwatches,
  Inline,
  Link,
  Placeholder,
  Stack,
  Text,
} from '@stitch-fix/mode-react';
import React, { useMemo } from 'react';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import Price from '../Price';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import AllSizesTracker from '../Gated/src/tracking/allSizesTracker';
import { Trans, loadTranslations } from '../../../shared/i18n';
import translations from './translations.json';
import ProductSizes from '../ProductSizes';
import StockStatus from '../StockStatus';

const { useTranslation } = loadTranslations(translations, 'ProductSelection');

const ProductSelection = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useProductDetailsPageContext();
  const { reportSelectEvent } = useSelectEvent();

  const showPlaceholder =
    state.loading ||
    state.recommendedSizesLoading ||
    state.shopRecommendedColorOptionsBySizeLoading ||
    !!state.clientLoading ||
    !state.priceAndInventoryData;

  const handleStyleProfileCtaClick = () =>
    AllSizesTracker.trackStyleProfileCtaClickEvent({
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      /* @ts-ignore */
      collection: state.collectionName,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      /* @ts-ignore */
      outfitId: state.outfitId,
      productSkuId: state.productSkuId,
      source: state.source,
    });

  const formattedColors = useMemo(
    () =>
      state.colorOptions?.map(colorOption => ({
        label: colorOption.label || '',
        hex: colorOption.swatch.hex || '',
        // empty URL values are passed in as `null`
        url: colorOption.swatch.url || undefined,
        uniqueColorId: colorOption.uniqueColorId || undefined,
        isOutOfStock:
          !colorOption.availabilityBySize[
            state.onRecommendedSizesTab
              ? state.recommendedSkuSizeLabel || ''
              : state.selectedSizeLabel || state.fallbackSizeLabel || ''
          ],
      })),
    [
      state.colorOptions,
      state.selectedSizeLabel,
      state.fallbackSizeLabel,
      state.onRecommendedSizesTab,
      state.recommendedSkuSizeLabel,
    ],
  );

  return showPlaceholder ? (
    <div data-testid="product-selection-placeholder">
      <Stack spacing={1} my={1}>
        <Placeholder width={4} />
        <Inline spacing={0.5}>
          <Placeholder variant="circle" width={2} height={2} />
          <Placeholder variant="circle" width={2} height={2} />
        </Inline>
        <Placeholder width={4} />
        <Inline spacing={0.5}>
          <Placeholder variant="rect" width={3} height={3} />
          <Placeholder variant="rect" width={3} height={3} />
          <Placeholder variant="rect" width={3} height={3} />
        </Inline>
        <Placeholder
          display={{ sm: 'none', md: 'block' }}
          variant="rect"
          width={12}
          height={3}
          my={2}
        />
      </Stack>
    </div>
  ) : (
    <>
      <Box mt={{ sm: 0.25, md: 0 }} mb={{ sm: 1, md: 1.5 }}>
        <Price
          value={state.formattedPrice}
          priceMarkdown={
            state.gated // TODO: The gated check can be removed when we release strikethrough pricing on ungated PDP
              ? state.priceAndInventoryData?.variantBySkuId?.priceMarkdown
              : null
          }
        />
      </Box>
      {/* Potential refactor:
          - Move Price (above) out of this component
          - Have ProductDetailsPage decide whether to render ProductSelection
      */}
      {((!state.clientBusinessLineIsMismatched &&
        !state.shoppingBagBusinessLineIsMismatched) ||
        state.eligibleForShoppingAcrossBusinessLines) && (
        <>
          <Box mb={{ sm: 0.5, md: 0.75 }}>
            <Text height="short" family="medium" scale="3">
              Color:{' '}
            </Text>
            <Text height="short" family="regular" scale="3">
              {state.selectedColorLabel || state.fallbackColorLabel}
            </Text>
          </Box>
          {!state.isStaticView && (
            <Box mb={{ sm: 1, md: 1.5 }}>
              {(state.selectedColorLabel || state.fallbackColorLabel) &&
                !state.recommendedSizesLoading &&
                !state.shopRecommendedColorOptionsBySizeLoading && (
                  // data-testid is used by the verify_skus rake task in addition to tests.
                  <div data-testid="color-swatches">
                    <ColorSwatches
                      colors={formattedColors}
                      selectedColor={
                        state.selectedUniqueColorId ||
                        state.selectedColorLabel ||
                        state.fallbackUniqueColorId ||
                        state.fallbackColorLabel ||
                        ''
                      }
                      onChange={(colorLabel, uniqueColorId) => {
                        reportSelectEvent({
                          schema: 'product_select',
                          name: 'color',
                          action_name: 'color_select',
                          external_product_id: `${state.externalProductId}`,
                          size: state.selectedSizeLabel,
                          unique_color_id: uniqueColorId,
                          inseam: state.selectedInseamLabel,
                          fit: null, // We don't currently track this
                        });

                        dispatch({
                          type: 'COLOR_SELECTED',
                          payload: {
                            colorLabel,
                            uniqueColorId,
                          },
                        });
                      }}
                      wrap
                    />
                  </div>
                )}
            </Box>
          )}
          <ProductSizes />
          {!state.isStaticView && state.showStockStatus && (
            <Box mb={1}>
              <StockStatus
                outOfStock={state.isSkuOutOfStock}
                stockMessage={
                  state.priceAndInventoryData?.variantBySkuId?.inventory
                    ?.stockMessage
                }
              />
            </Box>
          )}
          {!state.isStaticView && state.gated && state.showStyleProfileCta && (
            <>
              <Text setting="body-small" mt={1.5}>
                <strong>{t('styleProfile.heading')}</strong>
              </Text>
              <Text setting="body-small" mb={{ sm: 0, md: 0.75 }}>
                <Trans
                  t={t}
                  i18nKey="styleProfile.cta"
                  components={{
                    Link: (
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      /* @ts-ignore */
                      <Link
                        href="/style/profile"
                        onClick={handleStyleProfileCtaClick}
                        id="style-profile-link"
                      />
                    ),
                  }}
                />
              </Text>
            </>
          )}
        </>
      )}
    </>
  );
};

export default ProductSelection;
